import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import NumberRollup from "@/components/NumberRollup";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import Modal from "@/components/Modal";
import ConfettiBurst from "@/components/ConfettiBurst";
import {
  Flame, Medal, Crown, Trophy, Sparkles, Lock, ArrowRight,
  Snowflake, Shield, RotateCcw,
} from "lucide-react";
import TokenPill from "@/components/streak/TokenPill";
import HistoryHeatStrip from "@/components/streak/HistoryHeatStrip";
import ProTierCard from "@/components/streak/ProTierCard";
import ReferralCard from "@/components/streak/ReferralCard";
import StreakSavedCelebration from "@/components/streak/StreakSavedCelebration";

const ICON = { flame: Flame, medal: Medal, crown: Crown, trophy: Trophy };

// Milestone thresholds mirror STREAK_MILESTONES in backend server.py.
const MILESTONE_THRESHOLDS = [
  { months: 3,  name: "Consistent" },
  { months: 6,  name: "Disciplined" },
  { months: 12, name: "Legend" },
];

export default function Streak() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [saveTarget, setSaveTarget] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [celebration, setCelebration] = useState(null);
  const [exporting, setExporting] = useState(false);
  const shareCardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [milestoneBurst, setMilestoneBurst] = useState(0);
  const [milestoneJustHit, setMilestoneJustHit] = useState(null);
  const [rescueHandled, setRescueHandled] = useState(false);

  const load = useCallback(async () => {
    const [r, rr] = await Promise.all([api.get("/streak"), api.get("/referrals/me")]);
    setData(r.data);
    setReferrals(rr.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // "Rescue my streak" deep link (from the Weekly Recap email).
  useEffect(() => {
    if (rescueHandled || !data) return;
    if (searchParams.get("rescue") !== "true") return;
    const eligible = data.eligible_saves || [];
    if (data.tokens_available > 0 && eligible.length > 0) {
      setSaveTarget(eligible[0]);
      toast.info(t("toasts.rescue_pick_month"), { duration: 4000 });
    } else if (eligible.length === 0) {
      toast.success(t("toasts.nothing_to_rescue"), { duration: 4000 });
    } else {
      toast.error(t("toasts.no_save_tokens"));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("rescue");
    setSearchParams(next, { replace: true });
    setRescueHandled(true);
  }, [data, rescueHandled, searchParams, setSearchParams, t]);

  // Milestone crossings — confetti + toast at most once per tier (localStorage).
  useEffect(() => {
    if (!data) return;
    const current = Number(data.current_streak) || 0;
    let last = 0;
    try { last = Number(localStorage.getItem("pfo_streak_last_celebrated") || 0); }
    catch (e) { console.warn("streak celebration read failed", e); }

    const eligible = MILESTONE_THRESHOLDS
      .filter(m => current >= m.months && m.months > last)
      .sort((a, b) => b.months - a.months);

    if (eligible.length === 0) return;
    const target = eligible[0];
    setMilestoneJustHit(target);
    setMilestoneBurst(b => b + 1);
    try { localStorage.setItem("pfo_streak_last_celebrated", String(target.months)); }
    catch (e) { console.warn("streak celebration write failed", e); }
    toast.success(`Milestone reached · ${target.name} tier`, { duration: 5000 });
    setTimeout(() => setMilestoneJustHit(null), 6000);
  }, [data]);

  const spendSave = async () => {
    if (!saveTarget) return;
    setBusy(true);
    const streakBefore = data.current_streak;
    try {
      const { data: fresh } = await api.post("/streak/save", { year: saveTarget.year, month: saveTarget.month });
      setData(fresh);
      setCelebration({
        month_short: saveTarget.month_short,
        year: saveTarget.year,
        net: saveTarget.net,
        streak_before: streakBefore,
        streak_after: fresh.current_streak,
      });
      setSaveTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const refundSave = async () => {
    if (!refundTarget) return;
    setBusy(true);
    try {
      const { data: fresh } = await api.delete(`/streak/save/${refundTarget.ym}`);
      setData(fresh);
      setRefundTarget(null);
      toast.success(t("toasts.save_refunded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="text-sm text-[var(--text-muted)]">{t("common.loading_streak")}</div>;

  const cur = user?.currency || "USD";
  const progressToNext = data.next_milestone
    ? Math.min(1, data.current_streak / data.next_milestone.months)
    : 1;
  const eligible = data.eligible_saves || [];
  const canSave = data.tokens_available > 0 && eligible.length > 0;

  return (
    <div>
      <PageHeader
        subtitle={t("pages.streak.subtitle")}
        title={t("pages.streak.title")}
        action={
          <Link to="/wrapup" className="btn-ghost" data-testid="streak-wrapup-btn">
            <Sparkles size={16}/> {t("pages.streak.see_wrapup")}
          </Link>
        }
      />

      {/* Hero streak */}
      <div className="grid lg:grid-cols-3 gap-5 mb-8 stagger">
        <div className="lg:col-span-2 card-flat p-8 relative overflow-hidden" style={{
          background: "linear-gradient(135deg, #21422F 0%, #2D5A40 55%, #437A55 100%)",
          color: "#FDFCFB",
          borderColor: "transparent",
        }}>
          <div className="absolute -top-16 -right-10 h-56 w-56 rounded-full pointer-events-none"
               style={{ background: "radial-gradient(circle, rgba(224,158,80,0.30) 0%, rgba(224,158,80,0) 70%)" }}/>
          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2" style={{ opacity: 0.85 }}>
                <Flame size={18} color="#E09E50" className="flame-flicker"/>
                <div className="label-eyebrow" style={{ color: "#FDFCFB", opacity: 0.85 }}>{t("pages.streak.current_streak")}</div>
              </div>
              <TokenPill available={data.tokens_available} max={data.tokens_max} pro={data.pro_unlocked}/>
            </div>
            <div className="flex items-baseline gap-3 mt-3">
              <div className="font-mono font-medium" style={{ fontSize: "clamp(3rem,7vw,5.5rem)", letterSpacing: "-0.03em", lineHeight: 1 }} data-testid="streak-current">
                <NumberRollup value={data.current_streak} duration={900} />
              </div>
              <div style={{ opacity: 0.75 }}>{data.current_streak === 1 ? t("pages.streak.months_one") : t("pages.streak.months_other")}</div>
            </div>
            <div className="mt-4 text-sm" style={{ opacity: 0.85 }}>
              {data.current_streak === 0
                ? t("pages.streak.start_hint")
                : data.next_milestone
                  ? <>{t("pages.streak.unlock_hint", { count: data.months_to_next, name: data.next_milestone.name }).split("<1>")[0]}<span className="font-mono font-medium">{data.months_to_next}</span>{t("pages.streak.unlock_hint", { count: data.months_to_next, name: data.next_milestone.name }).split("</1>")[1]?.split("<2>")[0]}<span style={{ color: "#E09E50", fontWeight: 600 }}>{data.next_milestone.name}</span>.</>
                  : t("pages.streak.mastered")}
            </div>

            {data.next_milestone && (
              <div className="mt-5 max-w-md">
                <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(0,0,0,0.25)" }}>
                  <div style={{ height: "100%", width: `${progressToNext * 100}%`, background: "linear-gradient(90deg, #E09E50, #F4B597)", transition: "width 500ms" }}/>
                </div>
                <div className="mt-2 text-xs font-mono" style={{ opacity: 0.7 }}>
                  {t("pages.streak.months_progress", { current: data.current_streak, target: data.next_milestone.months })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card-surface p-6">
          <div className="label-eyebrow">{t("pages.streak.longest_streak")}</div>
          <div className="num-hero mt-3">{data.longest_streak}</div>
          <div className="text-sm text-[var(--text-muted)] mt-1">{t("pages.streak.months_last_12")}</div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <div>
              <div className="label-eyebrow">{t("pages.streak.win_rate")}</div>
              <div className="num-md mt-1">{data.win_rate}%</div>
            </div>
            <div>
              <div className="label-eyebrow">{t("pages.streak.tracked")}</div>
              <div className="num-md mt-1">{data.active_months} {t("pages.streak.months_short")}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Streak Save CTA */}
      {canSave && (
        <div className="card-flat p-6 mb-6 flex flex-wrap items-center justify-between gap-4 animate-fadeup" style={{
          background: "linear-gradient(120deg, rgba(224,158,80,0.14) 0%, rgba(224,158,80,0.04) 100%)",
          borderColor: "rgba(224,158,80,0.35)",
        }} data-testid="streak-save-cta">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0"
                 style={{ background: "rgba(224,158,80,0.2)" }}>
              <Shield size={22} color="#A16A25"/>
            </div>
            <div>
              <div className="font-medium text-base" style={{ fontFamily: "var(--font-heading)" }}>
                {eligible.length === 1
                  ? t("pages.streak.save_cta_one", { month_short: eligible[0].month_short, year: eligible[0].year })
                  : t("pages.streak.save_cta_many", { count: eligible.length })}
              </div>
              <div className="text-sm text-[var(--text-secondary)] mt-1 leading-relaxed">
                {t("pages.streak.save_cta_body", { tokens: data.tokens_available, plural: data.tokens_available === 1 ? "" : "s" }).split("<1>")[0]}<span className="font-mono font-medium">{data.tokens_available}</span>{t("pages.streak.save_cta_body", { tokens: data.tokens_available, plural: data.tokens_available === 1 ? "" : "s" }).split("</1>")[1]}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {eligible.map(e => (
              <button key={e.ym} className="btn-primary" onClick={() => setSaveTarget(e)} data-testid={`save-month-${e.ym}`}>
                <Snowflake size={15}/> {t("pages.streak.save_month", { month_short: e.month_short, year: e.year })}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* History heat strip */}
      <HistoryHeatStrip data={data} currency={cur} busy={busy} onRefund={setRefundTarget} />

      {/* Milestones */}
      <div className="mb-8">
        <div className="label-eyebrow mb-4">{t("pages.streak.milestones")}</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 stagger">
          {data.milestones.map(m => {
            const Icon = ICON[m.icon] || Flame;
            return (
              <div key={m.key} className={`card-flat p-5 relative overflow-hidden ${m.unlocked ? "" : "opacity-70"}`} data-testid={`milestone-${m.key}`}>
                {m.tier === "pro" && !m.unlocked && (
                  <div className="absolute top-3 right-3 badge badge-warn">PRO</div>
                )}
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${
                  m.unlocked ? "bg-[var(--accent-primary)] text-white" : "bg-[var(--bg-accent)] text-[var(--text-muted)]"
                }`}>
                  {m.unlocked ? <Icon size={22}/> : <Lock size={18}/>}
                </div>
                <div className="mt-4 font-medium text-base" style={{ fontFamily: "var(--font-heading)" }}>{m.name}</div>
                <div className="text-xs text-[var(--text-muted)] mt-1">{t("pages.streak.milestone_months", { count: m.months })}</div>
                <div className="mt-4 h-1.5 rounded-full bg-[var(--bg-accent)] overflow-hidden">
                  <div className="h-full bg-[var(--accent-primary)]" style={{ width: `${m.progress * 100}%` }}/>
                </div>
                <div className="mt-2 text-[11px] font-mono text-[var(--text-muted)]">
                  {m.unlocked ? t("pages.streak.unlocked") : `${Math.round(m.progress * 100)}%`}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ProTierCard data={data} />

      {/* Save confirmation modal */}
      <Modal open={!!saveTarget} onClose={() => !busy && setSaveTarget(null)} title={t("pages.streak.save_title")}>
        {saveTarget && (
          <div className="grid gap-4">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-[var(--bg-accent)]">
              <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #86A8D8, #6B8FBB)" }}>
                <Snowflake size={22} color="#fff"/>
              </div>
              <div>
                <div className="font-medium">{saveTarget.month_short} {saveTarget.year}</div>
                <div className="text-sm text-[var(--text-secondary)] mt-0.5">
                  {t("pages.streak.net_cashflow_l")}: <span className="num-md text-[var(--accent-danger)]">{formatMoney(saveTarget.net, cur)}</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              {t("pages.streak.save_hint", { left: data.tokens_available - 1, plural: data.tokens_available - 1 === 1 ? "" : "s" }).split("<1>")[0]}<span className="font-mono font-medium">{data.tokens_available - 1}</span>{t("pages.streak.save_hint", { left: data.tokens_available - 1, plural: data.tokens_available - 1 === 1 ? "" : "s" }).split("</1>")[1]}
            </p>
            <div className="flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setSaveTarget(null)} disabled={busy} data-testid="save-cancel-btn">{t("common.cancel")}</button>
              <button className="btn-primary" onClick={spendSave} disabled={busy} data-testid="save-confirm-btn">
                {busy ? t("pages.streak.saving_a") : <><Shield size={15}/> {t("pages.streak.spend_token_save")}</>}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Refund confirmation modal */}
      <Modal open={!!refundTarget} onClose={() => !busy && setRefundTarget(null)} title={t("pages.streak.refund_title")}>
        {refundTarget && (
          <div className="grid gap-4">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-[var(--bg-accent)]">
              <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-white border border-[var(--border-default)]">
                <RotateCcw size={22} color="var(--accent-danger)"/>
              </div>
              <div>
                <div className="font-medium">{refundTarget.month_short} {refundTarget.year}</div>
                <div className="text-sm text-[var(--text-secondary)] mt-0.5">
                  {t("pages.streak.reverting_to")} <span className="num-md text-[var(--accent-danger)]">{formatMoney(refundTarget.net, cur)}</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              {t("pages.streak.refund_hint")}
            </p>
            <div className="flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setRefundTarget(null)} disabled={busy} data-testid="refund-cancel-btn">{t("pages.streak.keep_save")}</button>
              <button className="btn-primary" style={{ background: "var(--accent-danger)", borderColor: "var(--accent-danger)" }}
                      onClick={refundSave} disabled={busy} data-testid="refund-confirm-btn">
                {busy ? t("pages.streak.refunding") : <><RotateCcw size={15}/> {t("pages.streak.refund_token")}</>}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {referrals && <ReferralCard referrals={referrals} />}

      <StreakSavedCelebration
        celebration={celebration}
        onClose={() => setCelebration(null)}
        cardRef={shareCardRef}
        exporting={exporting}
        setExporting={setExporting}
        referralCode={referrals?.code}
        userName={user?.name}
      />

      <ConfettiBurst trigger={milestoneBurst} />
      {milestoneJustHit && (
        <div
          className="fixed left-1/2 -translate-x-1/2 top-6 z-[9997] animate-fadeup"
          data-testid="milestone-banner"
          style={{
            background: "linear-gradient(135deg, #2D5A40 0%, #E09E50 100%)",
            color: "#FDFCFB",
            padding: "14px 22px",
            borderRadius: 999,
            boxShadow: "0 24px 60px -20px rgba(0,0,0,0.35)",
            display: "flex", alignItems: "center", gap: 12,
            fontFamily: "var(--font-heading)",
          }}
        >
          <Trophy size={18} />
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", opacity: 0.85 }}>
              {t("pages.streak.milestone_reached")}
            </div>
            <div style={{ fontSize: 16, fontWeight: 500, marginTop: 1 }}>
              {t("pages.streak.milestone_tier", { name: milestoneJustHit.name, months: milestoneJustHit.months })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
